import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowDown, ArrowUp, GalleryHorizontal, Plus, Trash2 } from 'lucide-react'
import { useSiteConfig, useUpdateSite } from '@/hooks/data/useSite'

function parseImages(json: string | undefined): string[] {
  if (!json) return []
  try {
    const parsed = JSON.parse(json)
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

function CarouselPage() {
  const site = useSiteConfig()
  const update = useUpdateSite()
  const [newUrl, setNewUrl] = useState('')

  const images = parseImages(site.data?.carouselImagesJson)

  const persist = (next: string[]) => {
    if (!site.data) return
    return update.mutateAsync({
      title: site.data.title,
      applicationName: site.data.applicationName,
      headerTitle: site.data.headerTitle,
      description: site.data.description,
      phone: site.data.phone,
      acceptingBookings: site.data.acceptingBookings,
      carouselImagesJson: JSON.stringify(next),
    })
  }

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= images.length) return
    const next = [...images]
    ;[next[index], next[target]] = [next[target], next[index]]
    persist(next)
  }

  const remove = (index: number) => persist(images.filter((_, i) => i !== index))

  const add = () => {
    const url = newUrl.trim()
    if (!url) return
    persist([...images, url])
    setNewUrl('')
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
        <GalleryHorizontal className="w-6 h-6" /> Homepage Carousel
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Reads/writes the real `Site.CarouselImagesJson` field (`/api/v1/site/public`'s
        `carouselImages`) — the actual source the homepage `ImageCarousel` renders from. This is
        a plain ordered list of image URLs, not per-slide title/subtitle/link content (that
        doesn't exist on the real entity).
      </p>

      {site.isLoading ? (
        <p className="p-6 text-gray-500">Loading slides…</p>
      ) : site.isError ? (
        <p className="p-6 text-red-600">Failed to load. Requires the manager role.</p>
      ) : (
        <>
          {images.length === 0 ? (
            <p className="p-6 text-gray-500">No slides yet.</p>
          ) : (
            <div className="space-y-2">
              {images.map((url, i) => (
                <div key={`${url}-${i}`} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                  <div className="w-20 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                    <img src={url} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0 text-sm text-gray-600 dark:text-gray-400 truncate">{url}</div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => move(i, 1)}
                      disabled={i === images.length - 1}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(i)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500" title="Remove slide">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <input
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://… image URL"
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white px-3 py-2 text-sm"
            />
            <button onClick={add} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50" disabled={!newUrl.trim()}>
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export const Route = createFileRoute('/(admin)/carousel/')({
  component: CarouselPage,
})
